import { useServerFlags } from "@/components/global/serverflags";
import { useSocket } from "@/components/global/socket";
import { ClientRequests } from "@/src/enum/socket";
import { GetWrapupType } from "@/src/server/models/seasonal/wrapup/modules/getwrapup";
import { useSession } from "next-auth/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface WrapupContextProps {
    getWrapup: (userIdParam: number) => Promise<void>;
    hasWrapup: boolean;
    wrapupData: GetWrapupType;
}

const WrapupContext = createContext<WrapupContextProps>({
    getWrapup: async (userIdParam: number) => { },
    hasWrapup: false,
    wrapupData: null,
});

export default function WrapupContextProvider({ children }) {
    const { socket } = useSocket();
    const { data: session } = useSession();
    const serverFlags = useServerFlags();
    const [hasWrapup, setWrapup] = useState<boolean>();
    const [wrapupData, setWrapupData] = useState<GetWrapupType>();

    const checkIfHasWrapup = useCallback(
        async () => {
            if (!socket || !session?.user?.id) {
                return;
            }

            if (serverFlags?.wrapup !== true) {
                return;
            }

            try {
                const result = await socket.emitWithAck(ClientRequests.HasWrapup);
                setWrapup(result);
            } catch (err) {
                console.error('Error fetching wrapupstatus', err);
                setWrapup(false);
            }
        },
        [setWrapup, serverFlags, session, socket],
    );

    const getWrapup = useCallback(
        async (userIdParam: number) => {
            if (!socket || !session?.user?.id) {
                return;
            }

            try {
                const result = await socket.emitWithAck(ClientRequests.GetWrapup, userIdParam);
                setWrapupData(result);
            } catch (err) {
                console.error('Error fetching wrapup', err);
                setWrapupData(null);
            }
        },
        [setWrapupData, session, socket],
    );

    useEffect(
        () => {
            checkIfHasWrapup();
        },
        [checkIfHasWrapup],
    );

    const value = useMemo(
        () => ({
            getWrapup: getWrapup,
            hasWrapup: hasWrapup,
            wrapupData: wrapupData,
        }),
        [
            getWrapup,
            hasWrapup,
            wrapupData,
        ],
    );

    return (
        <WrapupContext.Provider value={value}>
            {children}
        </WrapupContext.Provider>
    );
}

export function useWrapup() {
    return useContext(WrapupContext);
}
